import { useMemo } from 'react'

import {
  CreateRecordTypeMutation,
  CreateRecordTypeMutationVariables,
  GetAllFieldTemplatesInOrgQuery,
  GetAllFieldTemplatesInOrgQueryVariables,
  GetAllFieldTemplatesInRecordTypeQuery,
  GetAllFieldTemplatesInRecordTypeQueryVariables,
  GetRecordTypeSettingsQuery,
  GetRecordTypeSettingsQueryVariables,
  RecordTypeForRecordTemplatesQuery,
  RecordTypeForRecordTemplatesQueryVariables,
  RecordTypeOverviewQuery,
  RecordTypeOverviewQueryVariables,
  RecordTypeQuery,
  RecordTypeQueryVariables,
  RecordTypesOverviewQuery,
  RecordTypesOverviewQueryVariables,
  RecordTypesQuery,
  RecordTypesQueryVariables,
  RecordTypeStatusQuery,
  RecordTypeStatusQueryVariables,
  UpdateOverdueTaskDigestSubscribersMutation,
  UpdateOverdueTaskDigestSubscribersMutationVariables,
  UpdateRecordTypeMutation,
  UpdateRecordTypeMutationVariables,
} from 'types/graphql'

import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey, useMutation, useQuery, UseQueryOptions } from 'src/utils/queryClient'

const QueryRecordTypeStatus = gql`
  query RecordTypeStatusQuery($recordTypeId: Int!) {
    recordType: recordTypeById(id: $recordTypeId) {
      statuses {
        ...RecordTypeStatusFragment
      }
    }
  }
`

export const useRecordTypeStatusesQuery = (args: { recordTypeId: number | null | undefined }) => {
  return useQuery<RecordTypeStatusQuery, RecordTypeStatusQueryVariables>({
    queryKey: [QueryKey.RecordTypeStatuses, args.recordTypeId],
    queryDocument: QueryRecordTypeStatus,
    variables: {
      recordTypeId: args.recordTypeId ?? -1,
    },
    enabled: !!args.recordTypeId,
  })
}

const RecordTypes = gql`
  query RecordTypesQuery($organizationId: Int!) {
    recordTypesForOrg(organizationId: $organizationId) {
      id
      name
    }
  }
`
export const useRecordTypesQuery = (organizationId: number) => {
  return useQuery<RecordTypesQuery, RecordTypesQueryVariables>({
    queryKey: [QueryKey.RecordTypes, organizationId],
    queryDocument: RecordTypes,
    variables: { organizationId },
  })
}

const RecordType = gql`
  query RecordTypeQuery($recordTypeId: Int!) {
    recordType: recordTypeById(id: $recordTypeId) {
      ...RecordTypeFragment
    }
  }
`
export const useRecordTypeQuery = (recordTypeId: number | null | undefined) => {
  return useQuery<RecordTypeQuery, RecordTypeQueryVariables>({
    queryKey: [QueryKey.RecordType, recordTypeId],
    queryDocument: RecordType,
    variables: {
      recordTypeId: recordTypeId ?? -1,
    },
    enabled: !!recordTypeId,
  })
}

const RecordTypeForRecordTemplates = gql`
  query RecordTypeForRecordTemplatesQuery($recordTypeId: Int!) {
    recordType: recordTypeById(id: $recordTypeId) {
      ...ModulePageRecordTypeFragment
    }
  }
  fragment ModulePageRecordTypeFragment on RecordType {
    id
    recordTemplates {
      ...RecordTemplateForConfigFragment
    }
    archivedRecordTemplates {
      ...ArchivedRecordTemplateForConfigFragment
    }
    category
  }
  fragment RecordTemplateForConfigFragment on RecordTemplate {
    id
    recordTypeId
    name
    tags
    applicantType
    uuid
    prefix
    visibleOnModulePickPage
    visibleOnOrganizationHomepage
    owner {
      ...UserDataFragment
    }
    ownerUserFormula {
      ...FormulaOptionFragment
    }
    createdByUser {
      ...UserDataFragment
    }
    applicationGroup {
      id
      name
    }
  }
  fragment ArchivedRecordTemplateForConfigFragment on RecordTemplate {
    archivedAt
    archivedByUser {
      ...UserDataFragment
    }
    id
    name
    prefix
    uuid
  }
`

export const useRecordTypeForConfigQuery = (recordTypeId: number) => {
  return useQuery<RecordTypeForRecordTemplatesQuery, RecordTypeForRecordTemplatesQueryVariables>({
    queryDocument: RecordTypeForRecordTemplates,
    queryKey: [QueryKey.RecordTypeForRecordTemplates, recordTypeId],
    variables: { recordTypeId },
  })
}

const RecordTypesOverview = gql`
  query RecordTypesOverviewQuery($organizationId: Int!) {
    recordTypes(organizationId: $organizationId) {
      ...RecordTypeOverviewFragment
    }
  }
  fragment RecordTypeOverviewFragment on RecordType {
    id
    icon
    name
    recordName
    displayName
    category
    shouldEmailCitizens
    shouldEmailStaff
    shouldShowInStaffNavigation
    visibleOnOrganizationHomepage
    areMeetingsEnabled
  }
`

export const useRecordTypesOverviewQuery = () => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<RecordTypesOverviewQuery, RecordTypesOverviewQueryVariables>({
    queryDocument: RecordTypesOverview,
    queryKey: [QueryKey.RecordTypesOverview, organizationId],
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
  const recordTypes = useMemo(() => data?.recordTypes || [], [data])
  return {
    ...rest,
    recordTypes,
  }
}

const Create = gql`
  mutation CreateRecordTypeMutation($input: CreateRecordTypeInput!) {
    createRecordType(input: $input) {
      id
    }
  }
`
export const useCreateRecordTypeMutation = () => {
  return useMutation<CreateRecordTypeMutation, CreateRecordTypeMutationVariables>({
    mutationDocument: Create,
  })
}

const Update = gql`
  mutation UpdateRecordTypeMutation($id: Int!, $input: UpdateRecordTypeInput!) {
    updateRecordType(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateRecordTypeMutation = () => {
  return useMutation<UpdateRecordTypeMutation, UpdateRecordTypeMutationVariables>({
    mutationDocument: Update,
  })
}

const GetAllFieldTemplatesInRecordType = gql`
  query GetAllFieldTemplatesInRecordTypeQuery($recordTypeId: Int!) {
    recordType: recordTypeById(id: $recordTypeId) {
      recordTemplates {
        id
        name
        applicationFields {
          ...FieldTemplateListFragment
        }
      }
    }
  }
`
export const useAllFieldTemplatesInRecordTypeQuery = (recordTypeId?: number | null) => {
  return useQuery<
    GetAllFieldTemplatesInRecordTypeQuery,
    GetAllFieldTemplatesInRecordTypeQueryVariables
  >({
    queryKey: [QueryKey.RecordTypeFields, recordTypeId],
    queryDocument: GetAllFieldTemplatesInRecordType,
    variables: {
      recordTypeId: recordTypeId ?? -1,
    },
    enabled: !!recordTypeId,
  })
}

const GetAllFieldTemplatesInOrg = gql`
  query GetAllFieldTemplatesInOrgQuery($organizationId: Int) {
    recordTypesUserHasAccessTo(organizationId: $organizationId) {
      id
      name
      recordTemplates {
        id
        name
        applicationFields {
          ...FieldTemplateListFragment
        }
      }
    }
  }
`
export const useAllFieldTemplatesInOrgQuery = (
  options?: UseQueryOptions<GetAllFieldTemplatesInOrgQuery, GetAllFieldTemplatesInOrgQueryVariables>
) => {
  const organizationId = useMyOrgId()
  return useQuery<GetAllFieldTemplatesInOrgQuery, GetAllFieldTemplatesInOrgQueryVariables>({
    queryKey: [QueryKey.AllFieldTemplatesInOrg, organizationId],
    queryDocument: GetAllFieldTemplatesInOrg,
    variables: { organizationId },
    enabled: !!organizationId && (options?.enabled ?? true),
    ...options,
  })
}

const RecordTypeOverview = gql`
  query RecordTypeOverviewQuery($recordTypeId: Int!, $organizationId: Int!) {
    recordTypeById(id: $recordTypeId) {
      ...RecordTypeForModulePageFragment
    }
    availableFormulas: getFormulas(input: { organizationId: $organizationId }) {
      ...FormulaOptionFragment
    }
  }
  fragment RecordTypeForModulePageFragment on RecordType {
    id
    displayName
    icon
    addressEntryType
    automationTemplates {
      ...AutomationTemplateFragment
    }
    issuedIdentifierAlgorithm
    moduleType
    visibleOnOrganizationHomepage
    connectedFormulas {
      ...FormulaOptionFragment
    }
    recordSettings {
      ...RecordSettingsFragment
    }
    recordTemplatesWithExpirationAutomationTemplate {
      id
      name
      uuid
    }
    allowInspections
    allowFees
    allowPlanReviews
    applyDescription
    areMeetingsEnabled
    displayName
    icon
    icon
    isContact
    name
    recordName
    shouldEmailCitizens
    shouldEmailStaff
    shouldShowInStaffNavigation
    overdueTaskDigestSubscribers {
      id
      user {
        ...UserDataFragment
      }
    }
    isUsingOrganizationDefaultLetterhead
    organization {
      id
      slug
    }
  }
`

export const useRecordTypeOverviewQuery = (recordTypeId: number) => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<RecordTypeOverviewQuery, RecordTypeOverviewQueryVariables>({
    queryDocument: RecordTypeOverview,
    queryKey: [QueryKey.RecordTypeOverview, recordTypeId],
    variables: { recordTypeId, organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
  const availableFormulas = useMemo(() => data?.availableFormulas || [], [data])
  return {
    availableFormulas,
    recordType: data?.recordTypeById,
    ...rest,
  }
}

const GetRecordTypeSettings = gql`
  query GetRecordTypeSettingsQuery($recordTypeId: Int!) {
    recordTypeById(id: $recordTypeId) {
      id
      recordSettingsDerived {
        ...OverridableRecordSettingsFragment
      }
      recordSettingsDerivedSources {
        ...OverridableRecordSettingSourcesFragment
      }
    }
  }
`

export const useRecordTypeSettingsQuery = (
  recordTypeId: number,
  options?: UseQueryOptions<GetRecordTypeSettingsQuery, GetRecordTypeSettingsQueryVariables>
) => {
  const { data, ...rest } = useQuery<
    GetRecordTypeSettingsQuery,
    GetRecordTypeSettingsQueryVariables
  >({
    queryKey: [QueryKey.RecordTypeSettings, recordTypeId],
    queryDocument: GetRecordTypeSettings,
    variables: {
      recordTypeId,
    },
    ...options,
  })

  const recordSettings = useMemo(
    () => data?.recordTypeById?.recordSettingsDerived ?? {},
    [data?.recordTypeById?.recordSettingsDerived]
  )
  const recordSettingsSources = useMemo(
    () => data?.recordTypeById?.recordSettingsDerivedSources ?? {},
    [data?.recordTypeById?.recordSettingsDerivedSources]
  )

  return {
    recordSettings,
    recordSettingsSources,
    ...rest,
  }
}

const UpdateOverdueTaskDigestSubscribers = gql`
  mutation UpdateOverdueTaskDigestSubscribersMutation(
    $input: UpdateOverdueTaskDigestSubscribersInput!
  ) {
    updateOverdueTaskDigestSubscribers(input: $input) {
      id
    }
  }
`

export const useUpdateOverdueTaskDigestSubscribersMutation = () => {
  return useMutation<
    UpdateOverdueTaskDigestSubscribersMutation,
    UpdateOverdueTaskDigestSubscribersMutationVariables
  >({ mutationDocument: UpdateOverdueTaskDigestSubscribers })
}
